/**
 * Gift Selection Store - Core store definition
 * Persistent session management with multi-turn support
 *
 * ARCHITECTURE: Three-layer pattern
 * 1. Store Layer (this file) - Raw observable definition
 * 2. Actions Layer (gift-selection-store.actions.ts) - Internal state access and mutations
 * 3. Public API Layer (gift-selection-store.public-api.ts) - External interface (ONLY way to access store)
 *
 * USAGE:
 * - External code should ONLY use giftSelectionStoreAPI from gift-selection-store.public-api.ts
 * - Direct access to giftSelectionSessions$ is FORBIDDEN for external code
 *
 * Entity can be:
 * - Product ID (for product-level gifts)
 * - Cart ID (for cart-level gifts)
 */
import { type Observable, batch } from '@legendapp/state';
import { createPersistedStore } from '@kidslabs/module-core';
import { type FreeGiftsData } from '../../../types/front.graphql';
import { type GiftSelectionTurn, type SelectedGift } from '../types/gift-selection.types';
import { uuidGenerator } from '@kidslabs/module-quote';

// ============================================================================
// TYPES
// ============================================================================

export type GiftSelectionEntityType = 'product' | 'cart';

/**
 * Gift Selection Session with multi-turn support
 */
export interface GiftSelectionSession {
    /**
     * Entity ID - can be productId or cartId
     * - For product gifts: productId
     * - For cart gifts: cartId
     */
    entityId: string;

    /**
     * Type of entity this session is for
     */
    entityType: GiftSelectionEntityType;

    /**
     * Product quantity - only relevant for product-level gifts
     */
    productQuantity?: number;

    /**
     * Unique session identifier
     */
    sessionId: string;

    /**
     * Maximum number of selection turns allowed in this session
     * Default: 3 turns
     */
    maxSelectionTurns: number;

    /**
     * Array of selection turns
     * Each turn represents one independent gift selection with suggested and selected gifts
     */
    selectionTurns: GiftSelectionTurn[];

    /**
     * All available gift rules for this session
     */
    availableGifts: FreeGiftsData[];

    /**
     * Flat array of all selected gifts across all turns
     * Synchronized with selectionTurns for convenience
     */
    selectedGifts: SelectedGift[];

    // UI state
    isPopupOpen: boolean;
    showGiftOptionsForTurn?: string; // Show options modal for specific turnId

    // Metadata
    createdAt: number;
    lastUpdated: number;
}

/**
 * Store structure with fixed property names
 * This avoids dynamic keys at root level for better manageability
 */
export interface GiftSelectionStoreState {
    /**
     * All gift selection sessions indexed by sessionId
     */
    sessions: Record<string, GiftSelectionSession>;

    /**
     * Active session IDs for each entity
     * Structure: { entityType: { entityId: sessionId } }
     * Example: { product: { "123": "product-123-uuid" }, cart: { "456": "cart-456-uuid" } }
     */
    activeSessionIds: Record<GiftSelectionEntityType, Record<string, string>>;
}

// ============================================================================
// STORE DEFINITION
// ============================================================================

/**
 * Store containing all gift selection sessions
 * Uses createPersistedStore for IndexedDB persistence on web,
 * matching mobile's MMKV persistence pattern via syncPluginStore.
 *
 * Structure: giftSelectionStore$.sessions[sessionId]
 * This provides a fixed property 'sessions' instead of dynamic keys at root
 *
 * NOTE: This should only be accessed through the public API layer
 */
export const giftSelectionStore$ = createPersistedStore<GiftSelectionStoreState>('giftSelection', {
    sessions: {},
    activeSessionIds: {
        product: {},
        cart: {},
    },
});

// ============================================================================
// INTERNAL HELPER FUNCTIONS
// ============================================================================

/**
 * Generate unique session key
 * Format: {entityType}-{entityId}-{uuid}
 */
const generateSessionKey = (entityType: GiftSelectionEntityType, entityId: string): string => {
    const uuid = uuidGenerator.generate();
    return `${entityType}:${entityId}:${uuid}`;
};

/**
 * Get all sessions for a specific entity
 * Centralized function to avoid duplicate iteration logic
 *
 * @param entityId - ID of the entity (product or cart)
 * @param entityType - Type of entity
 * @returns Array of [sessionId, session] tuples matching the entity
 */
const getSessionsByEntity = (
    entityId: string,
    entityType: GiftSelectionEntityType
): Array<[string, GiftSelectionSession]> => {
    const allSessions = giftSelectionStore$.sessions.peek();
    const matchingSessions: Array<[string, GiftSelectionSession]> = [];

    for (const sessionId in allSessions) {
        if (Object.prototype.hasOwnProperty.call(allSessions, sessionId)) {
            const session = allSessions[sessionId];
            if (session.entityId === entityId && session.entityType === entityType) {
                matchingSessions.push([sessionId, session]);
            }
        }
    }

    return matchingSessions;
};

// ============================================================================
// CORE FUNCTIONS (Used by actions layer)
// ============================================================================

/**
 * Create a new gift selection session and add to persistent store
 *
 * @param entityId - ID of the product or cart
 * @param entityType - Type of entity ('product' or 'cart')
 * @param availableGifts - List of available gift rules
 * @param maxTurns - Maximum number of selection turns (default: 3)
 * @param productQuantity - Product quantity (for product-level gifts)
 * @returns Session ID of the newly created session
 */
export const createGiftSelectionSession = (
    entityId: string,
    entityType: GiftSelectionEntityType,
    availableGifts: FreeGiftsData[],
    maxTurns: number = 3,
    productQuantity?: number
): string => {
    const sessionId = generateSessionKey(entityType, entityId);

    const newSession: GiftSelectionSession = {
        entityId,
        entityType,
        sessionId,
        productQuantity: productQuantity ?? (entityType === 'product' ? 1 : undefined),
        maxSelectionTurns: maxTurns,
        selectionTurns: [],
        selectedGifts: [],
        availableGifts,
        isPopupOpen: false,
        createdAt: Date.now(),
        lastUpdated: Date.now(),
    };

    batch(() => {
        giftSelectionStore$.sessions[sessionId].set(newSession);
        giftSelectionStore$.activeSessionIds[entityType][entityId].set(sessionId);
    });

    return sessionId;
};

/**
 * Restore a gift selection session from server-synced data.
 * Unlike createGiftSelectionSession which generates a new sessionId,
 * this function uses the server-provided sessionId to create a minimal session entry
 * so that the gift selection UI can bind to it and populate gifts lazily.
 *
 * @param serverSessionId - The session ID from the server (logic.sessionId)
 * @param entityId - Entity identifier (productId or cartId)
 * @param entityType - Type of entity ('product' or 'cart')
 * @param productQuantity - Product quantity (for product-level gifts)
 */
export const restoreServerSession = (
    serverSessionId: string,
    entityId: string,
    entityType: GiftSelectionEntityType,
    productQuantity?: number
): void => {
    // Skip if session already exists
    if (giftSelectionStore$.sessions[serverSessionId]?.peek()) {
        return;
    }

    const newSession: GiftSelectionSession = {
        entityId,
        entityType,
        sessionId: serverSessionId,
        productQuantity: productQuantity ?? (entityType === 'product' ? 1 : undefined),
        maxSelectionTurns: 3,
        selectionTurns: [],
        selectedGifts: [],
        availableGifts: [],
        isPopupOpen: false,
        createdAt: Date.now(),
        lastUpdated: Date.now(),
    };

    batch(() => {
        giftSelectionStore$.sessions[serverSessionId].set(newSession);
        giftSelectionStore$.activeSessionIds[entityType][entityId].set(serverSessionId);
    });
};

/**
 * Get the active session observable for a specific entity
 * Looks up the active sessionId and returns the session observable from sessions
 * @param entityType - Type of entity ('product' or 'cart')
 * @param entityId - Entity identifier
 * @returns Observable of the active session, or undefined if not found
 */
export const getActiveSession$ = (
    entityType: GiftSelectionEntityType,
    entityId: string
): Observable<GiftSelectionSession> | undefined => {
    const activeSessionId = giftSelectionStore$.activeSessionIds[entityType]?.[entityId]?.peek();

    if (!activeSessionId) {
        return undefined;
    }

    return giftSelectionStore$.sessions[activeSessionId];
};

/**
 * Delete a session from the store by sessionId
 */
export const cleanupGiftSelectionSession = (sessionId: string): void => {
    const session = giftSelectionStore$.sessions[sessionId].peek();
    if (session) {
        giftSelectionStore$.sessions[sessionId].delete();
    }
};

/**
 * Delete all sessions for a specific entity
 */
export const cleanupEntitySessions = (entityId: string, entityType: GiftSelectionEntityType): void => {
    const matchingSessions = getSessionsByEntity(entityId, entityType);

    if (matchingSessions.length === 0) {
        return;
    }

    batch(() => {
        // Delete all sessions for this entity
        matchingSessions.forEach(([sessionId]) => {
            giftSelectionStore$.sessions[sessionId].delete();
        });

        // Clear active session reference
        if (giftSelectionStore$.activeSessionIds[entityType]?.[entityId]?.peek()) {
            giftSelectionStore$.activeSessionIds[entityType][entityId].delete();
        }
    });
};

/**
 * Delete all sessions (useful when user logs out)
 */
export const cleanupAllGiftSelectionSessions = (): void => {
    batch(() => {
        giftSelectionStore$.sessions.set({});
        giftSelectionStore$.activeSessionIds.set({
            product: {},
            cart: {},
        });
    });
};

// ============================================================================
// BACKWARD COMPATIBILITY EXPORTS
// These functions maintain backward compatibility with existing code
// They delegate to the new architecture internally
// ============================================================================

/**
 * Get or create a session for an entity
 * Returns the observable for the session
 */
export const getGiftSelectionStore$ = (
    entityId: string,
    entityType: GiftSelectionEntityType = 'product',
    forceNewSession: boolean = false
): Observable<GiftSelectionSession> => {
    if (forceNewSession) {
        const sessionId = createGiftSelectionSession(entityId, entityType, [], 3);
        return giftSelectionStore$.sessions[sessionId];
    }

    const existingSession = getActiveSession$(entityType, entityId);
    if (existingSession) {
        return existingSession;
    }

    const sessionId = createGiftSelectionSession(entityId, entityType, [], 3);
    return giftSelectionStore$.sessions[sessionId];
};

/**
 * Create a new session for an entity
 * Returns the observable for the session
 */
export const createNewGiftSelectionSession$ = (
    entityId: string,
    entityType: GiftSelectionEntityType = 'product'
): Observable<GiftSelectionSession> => {
    const sessionId = createGiftSelectionSession(entityId, entityType, [], 3);
    return giftSelectionStore$.sessions[sessionId];
};

/**
 * Cleanup gift selection store for a specific entity
 * Alias for cleanupEntitySessions
 */
export const cleanupGiftSelectionStore = (entityId: string, entityType: GiftSelectionEntityType = 'product'): void => {
    cleanupEntitySessions(entityId, entityType);
};
